import { computed, nextTick, toValue, type MaybeRefOrGetter } from 'vue';

// Accessibility fixes for PrimeVue's DatePicker in view="month" (month/year only), shared by every place
// that renders one. Spread `pt` and bind `appendTo`, `onShow` and `onHide` onto the DatePicker:
//
//   const monthPicker = useAccessibleMonthPicker(() => 'dob', () => describedByIds.value);
//   <DatePicker input-id="dob" view="month" :pt="monthPicker.pt.value" :append-to="monthPicker.appendTo"
//     @show="monthPicker.onShow" @hide="monthPicker.onHide" />
//
// What it fixes, and why:
// - appendTo 'self': PrimeVue teleports the panel to the end of <body>, so Tab from the field skips it and
//   screen reader browse/scan mode only reaches it after the whole rest of the page. In place, it's next.
// - Month/year cells are bare <span>s, so a screen reader in browse/scan mode has nothing to activate and
//   Enter does nothing. role="button" makes them invokable; the label adds the year ("December 1995")
//   since the bare "Dec" gives no context; aria-pressed/aria-disabled expose selected and unavailable
//   (e.g. too-young) cells that are otherwise only shown visually.
// - The header title is a live region so moving to another year/decade is announced.
// - PrimeVue hardcodes inputmode="none" on the input, hiding the on-screen keyboard on mobile.
// - On open, PrimeVue leaves focus on the input, so nothing tells a screen reader user the panel opened.
//   onShow moves focus onto the selected month (or first selectable one); onHide hands it back to the input
//   when the panel closes from inside it (Escape is already handled by PrimeVue).
export function useAccessibleMonthPicker(inputId: MaybeRefOrGetter<string>, describedBy?: MaybeRefOrGetter<string | undefined>) {
  let panelEl: HTMLElement | null = null;

  const pt = computed(() => ({
    pcInputText: {
      root: {
        inputmode: 'text',
        'aria-describedby': toValue(describedBy) || undefined,
      },
    },
    title: { 'aria-live': 'polite' },
    monthView: { onVnodeMounted: ({ el }: VNodeWithEl) => focusViewCell(el, 'month') },
    yearView: { onVnodeMounted: ({ el }: VNodeWithEl) => focusViewCell(el, 'year') },
    month: ({ context, instance }: MonthPtOptions) => ({
      role: 'button',
      'aria-label': `${fullMonthName(context.monthIndex)} ${instance.currentYear}`,
      'aria-pressed': context.selected,
      'aria-disabled': context.disabled,
    }),
    year: ({ context }: YearPtOptions) => ({
      role: 'button',
      'aria-label': String(context.year.value),
      'aria-pressed': context.selected,
      'aria-disabled': context.disabled,
    }),
    // PrimeVue puts a visually hidden aria-live copy of the label inside the selected cell, which shows up
    // as two extra stops in browse/scan mode. aria-pressed and focus returning to the updated input
    // already convey the selection, so hide it.
    hiddenMonth: { 'aria-hidden': 'true' },
    hiddenYear: { 'aria-hidden': 'true' },
  }));

  function getPanel() {
    const panelId = document.getElementById(toValue(inputId))?.getAttribute('aria-controls');
    return panelId ? document.getElementById(panelId) : null;
  }

  function onShow() {
    nextTick(() => {
      panelEl = getPanel();
      if (panelEl) focusCell(panelEl, 'month');
    });
  }

  // Only reclaim focus if it was inside the panel - not when the user clicked or tabbed elsewhere
  function onHide() {
    const hadFocusInPanel = !!panelEl?.contains(document.activeElement);
    panelEl = null;
    if (hadFocusInPanel) nextTick(() => document.getElementById(toValue(inputId))?.focus());
  }

  return {
    pt,
    appendTo: 'self' as const,
    onShow,
    onHide,
    getPanel,
  };
}

// Focus the selected cell of a view, else its first selectable one
function focusCell(container: HTMLElement, section: 'month' | 'year') {
  const cell =
    container.querySelector<HTMLElement>(`[data-pc-section="${section}"][data-p-selected="true"]`) ??
    container.querySelector<HTMLElement>(`[data-pc-section="${section}"]:not([data-p-disabled="true"])`);
  if (!cell) return;
  cell.tabIndex = 0;
  cell.focus();
}

// Switching between the month and year views (the header's year button, or picking a year) unmounts the
// focused element and PrimeVue doesn't move focus anywhere, dropping it to <body>. When a view mounts with
// focus lost like that (or still inside the panel), put it on that view's cell. On first open focus is on
// the input instead, which onShow handles.
function focusViewCell(viewEl: HTMLElement | null, section: 'month' | 'year') {
  if (!viewEl) return;
  const active = document.activeElement;
  const panel = viewEl.closest<HTMLElement>('[data-pc-section="panel"]');
  if (active && active !== document.body && !panel?.contains(active)) return;
  focusCell(viewEl, section);
}

interface VNodeWithEl {
  el: HTMLElement | null;
}

interface MonthPtOptions {
  context: { monthIndex: number; selected: boolean; disabled: boolean };
  instance: { currentYear: number };
}

interface YearPtOptions {
  context: { year: { value: number }; selected: boolean; disabled: boolean };
}

function fullMonthName(monthIndex: number) {
  return new Date(2000, monthIndex, 1).toLocaleDateString('en-US', { month: 'long' });
}
